/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataType } from '@grapecity/wijmo';
import { Button } from '@mui/material';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import MPCommonSearch from './base/MPCommonSearch';
import { MpItem, MpItemCondition } from 'models/mp/MpItem';
import { CellType, DataMap } from '@grapecity/wijmo.grid';
import {
  findTrackingStatHe,
  findTrackingStatHis,
  saveMpSftyClsf,
  selectMpTracking,
} from 'apis/mp/MpTracking';
import MpMyBookmarkModal from './MpMyBookmarkModal';
import { BizName, ManagementMode } from 'models/common/Common';
import AutoSendEmailModal from 'components/modals/common/AutoSendEmailModal';
import { BmkAtcTpCd } from 'models/common/Bookmark';
import CustomGrid from 'components/grids/CustomGrid';
import { MpFileImgViewPopUp } from './popup/MpFileImgViewPopUp';
import { CommonUtil } from '../../utils/CommonUtil';
import useSessionStore from '../../stores/useSessionStore';
import { Code } from '../../models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from '../../apis/admin/CommonCode';
import * as input from '@grapecity/wijmo.input';
import { useCommonModal } from '../../hooks/useCommonModal';
import useEvent from 'react-use-event-hook';
import { MpSftyClsfPopUp } from './popup/MpSftyClsfPopUp';
import { findMpItems, findMpItemStatHis } from '../../apis/mp/MpItem';

import * as XLSX from 'xlsx';
import { getMpMgrChgHistoryList } from '../../apis/mp/MpMrChg';
import { MpMgrChgList } from '../../models/mp/MpMgr';
import { MpItemTracking } from '../../models/mp/MpTracking';
import { CrudCode } from '../../models/common/Edit';
import guideWijmo from '../sample/wijmo/GuideWijmo';
import { getExcelFileName } from '../../utils/ExcelUtil';
import { saveAs } from 'file-saver';

import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import * as wjXlsx from '@grapecity/wijmo.xlsx';
import _, { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import { hasRole } from '../../utils/SessionUtil';

interface FlexGridXlsxOptionsFixed extends wjGridXlsx.IFlexGridXlsxOptions {
  workbook?: wjXlsx.Workbook;
  includeColumnHeadersMerged?: boolean;
}

/**
 * 수평전개 진척형황
 * @constructor
 */
const MpTrackingManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const gridRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const flexGridHis = useRef<any>();
  const flexGridHe = useRef<any>();
  const flexGridItemList = useRef<any>();

  const { openCommonModal } = useCommonModal();
  const { langCd } = useSessionStore();
  const [rowData, setRowData] = useState<any[]>([]);
  const [rowDataMpSftyClsf, setRowDataMpSftyClsf] = useState<MpItemTracking[]>([]);

  const [mpItemCondition, setMpItemCondition] = useState<MpItemCondition>({
    startDataInsDtm: dayjs().add(-2, 'year').format('YYYY.MM.DD'),
    endDataInsDtm: dayjs().format('YYYY.MM.DD'),
    mpClsfCd: useSessionStore.getState().mpClsfType || '',
  });

  const [rowDataItemList, setRowDataItemList] = useState<any[]>([]);
  const [rowDataHis, setRowDataHis] = useState<MpItem[]>([]);
  const [rowDataHeExport, setRowDataHeExport] = useState<MpItemTracking[]>([]);

  const [condition, setCondition] = useState<MpItemCondition>({});
  const [dynamicCol, setDynamicCol] = useState<any[]>([]);
  const [excelData, setExcelData] = useState<any>();
  const [mpMyBookmarkModalOpen, setMpMyBookmarkModalOpen] = useState<boolean>(false);
  const [isOpenEmailModal, setOpenEmailModal] = useState<boolean>(false);
  const [isOpenFileImgViewModal, setOpenFileImgViewModal] = useState<boolean>(false);
  const [isOpenSftyClsfModal, setOpenSftyClsfModal] = useState<boolean>(false);
  const [getMpsftyClsf, setMpsftyClsf] = useState<string>('');
  const [getMpItemId, setMpItemId] = useState<string>('');

  const [getOenFileImgViewAtchFileGrId, setOpenFileImgViewAtchFileGrId] = useState<string>('');

  const [dataMapMpSftyClsf, setDataMapMpSftyClsf] = useState<Code[]>([]);
  const [dataMapPrdnProcCd, setDataMapPrdnProcCd] = useState<Code[]>([]);
  const [baseRemarks, setBaseRemarks] = useState<Code[]>([]);

  const [code, setCode] = useState<any>();

  const [isMpMntAuth, setMpMntAuth] = useState<boolean>(false);

  useEffect(() => {
    // 권한 체크
    if (hasRole('MP_MNT') || hasRole('ADM')) {
      setMpMntAuth(true);
    }
    getCommonCodes();
  }, []);

  const getCommonCodes = async () => {
    const baseRemarks: Code[] = await getCommonCodeNames('MP_ITEM_STAT_CD');
    const mpSftyClsf: Code[] = await getCommonCodeNames('MP_SFTY_CLSF');
    const mpClsfCd: Code[] = await getCommonCodeNames('MP_CLSF_CD'); // MP분류
    const prdnProcCd: Code[] = await getCommonCodeNamesCondition({
      cmnGrCd: 'PRDN_PROC_CD',
      optValCtn2: 'Y',
      // optValCtn6: optValCtn1, // [25.08.25] 공정 > MP분류에 따른 옵션 조건 사용안함 (ex. CMN, UT 등)
    });

    setDataMapMpSftyClsf(mpClsfCd);
    setDataMapPrdnProcCd(prdnProcCd);
    setBaseRemarks(baseRemarks);

    setCode({
      ...code,
      baseRemarks: baseRemarks,
      mpSftyClsf: mpSftyClsf,
    });
  };

  // const [checkedItems, setCheckedItems] = useState([]);
  // const onCheckedItemsChanged = useEvent((sender) => {
  //   setCheckedItems(sender.checkedItems);
  // });

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.collectionView.refresh();
    }
  }, [rowData]);

  const onCellEditEnded = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    // 미터유형이 변경된 경우
    if ('MP_SFTY_CLSF' === binding) {
      const item = grid.rows[e.row].dataItem;
      const col = grid.columns[e.col];
      const ms = col.editor as input.MultiSelect;
      const ch = ms.checkedItems;
      const dt = ch.map((v) => ({
        itemId: item.ITEM_ID,
        mpClsfCd: v.cmnCd,
      }));
      handleSave(dt);
    }
  });

  // const onItemFormatter = useEvent((panel, row, col, cell) => {
  //   if (CellType.Cell === panel.cellType) {
  //     const binding = panel.columns[col].binding;
  //     const item = panel.rows[row].dataItem;
  //     const isReadOnly = item.crudKey !== CrudCode.CREATE;
  //     if ('MP_SFTY_CLSF' === binding) {
  //       const match = code?.mpClsfCd.find((c) => c.cmnCd === item.MP_SFTY_CLSF);
  //       console.log(code?.mpClsfCd);
  //       if (match) {
  //         cell.textContent = match.cmnCdNm;
  //       }
  //     }
  //   }
  // });

  const initializedHis = (grid) => {
    flexGridHis.current = grid;
  };
  const initializedHe = (grid) => {
    flexGridHe.current = grid;
  };
  const initializedItemList = (grid) => {
    flexGridItemList.current = grid;
  };

  const initialized = (grid) => {
    gridRef.current = grid;
    setExcelData(grid);
    setFlexRef(grid);
    grid.rowHeaders.columns.clear();

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;

      const ht = grid.hitTest(e);
      if (ht.panel === grid.cells) {
        const binding = grid.columns[ht.col].binding;
        const item = grid.rows[ht.row].dataItem;
        setOpenFileImgViewAtchFileGrId('');

        //개조/사양서 미리 보기 팝업 open
        if ('ATCH_FILE_GR_ID' === binding) {
          if (item.ATCH_FILE_GR_ID_RPT_IMG_CNT > 0) {
            setOpenFileImgViewAtchFileGrId(item.ATCH_FILE_GR_ID_RPT);
            setOpenFileImgViewModal(true);
          }
        }
      }

      if (ht.row < 0 || ht.col < 8) return;
      if (grid.cells.getCellData(ht.row, ht.col, true).length <= 0) return;

      const hdr = grid.cells.columns[ht.col]._binding._key.replace("'", '').split(':');

      navigate('/mp/item-management', {
        state: {
          upprMnuUrl: '/mp/item', // 고정
          mode: ManagementMode.MODIFY, // 고정
          mnuId: `/mp/item/${ManagementMode.MODIFY}`, // 고정
          mnuNm: t('mp.label.Item 수정', 'Item 수정'), // 고정
          itemId: grid.rows[ht.row]._data.ITEM_ID, // 값 변경
          itemTpCd: grid.rows[ht.row]._data.ITEM_TP_CD, // 값 변경
          copCd: grid.rows[ht.row]._data.COP_CD, // 값 변경
          plntCd: hdr[0], // 값 변경
          lnId: hdr[1], // 값 변경
        },
      }); //매개변수 확인 필요
    });
  };

  const setColor = (ctx) => {
    if (ctx.text.length == 0) return '';
    const type = baseRemarks?.findIndex((o) => o.cmnCd === ctx.value);
    if (type === -1) {
      return `<span>{ctx.value}</span>`;
    } else {
      const remark = baseRemarks[type];
      return `<span class="${remark.optValCtn1}">${remark.cmnCdNm}</span>`;
    }
  };

  const exportGrids = async () => {
    const mainWorkbook = new wjXlsx.Workbook();
    const grids = [flexGridItemList.current, flexGridHe.current, flexGridHis.current];
    const sheetNames = ['Item List', '수평전개 현황', '상태 변경 이력'];

    for (let i = 0; i < grids.length; i++) {
      const tempWorkbook = wjGridXlsx.FlexGridXlsxConverter.save(grids[i], {
        includeColumnHeaders: true,
        includeRowHeaders: true,
        includeColumnHeadersMerged: true,
        includeCellStyles: true,
      } as FlexGridXlsxOptionsFixed);

      const tempSheet = tempWorkbook.sheets[0];
      tempSheet.name = sheetNames[i];
      mainWorkbook.sheets.push(tempSheet);
    }
    mainWorkbook.saveAsync('수평전개현황.xlsx');
  };

  const exportExcel = async () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
      formatItem: exportFormat,
    });
    book.sheets[0].name = '수평전개 Item';
    book.saveAsync(getExcelFileName(t('mp.label.수평전개 Item', '수평전개 Item')));
  };

  const exportFormat = (args) => {
    const p = args.panel;
    const col = args.col;
    const xlsxCell = args.xlsxCell;
    if (p.columns[col].binding) {
      const cell = args.getFormattedCell();
      xlsxCell.value = cell.textContent;
    }
  };

  const handleSearchExcelItemList = async (condition: MpItemCondition) => {
    setCondition(condition);
    const response = await findMpItems(condition);
    if (response) {
      const rowDataItemList = response || [];
      setRowDataItemList(rowDataItemList);
    }
  };

  const handleSearch = async (condition: MpItemCondition) => {
    //setMpItemCondition(condition);
    const response = await selectMpTracking(condition);
    if (response) {
      const rowData = response.list[0]?.list || [];
      const dynamicCol = response.list[0]?.col || [];
      console.log('rowData', rowData);
      setRowData(
        rowData.map((row) => ({
          ...row,
          MP_SFTY_CLSF: (code?.mpSftyClsf || []).filter((o) =>
            (row?.MP_SFTY_CLSF || '').split(',').includes(o.cmnCd)
          ),
        }))
      );
      setDynamicCol(dynamicCol);
      handleSearchExcelItemList(condition); //item list
      getSearchHis(condition); // history list
      getSearchHe(condition); //he export
    }
  };

  const getSearchHe = (condition) => {
    findTrackingStatHe(condition).then((result) => {
      setRowDataHeExport(result);
    });
  };

  const getSearchHis = (condition) => {
    findTrackingStatHis(condition).then((result) => {
      setRowDataHis(result);
    });
  };

  const handleSave = async (vo) => {
    saveMpSftyClsf(vo).then((result) => {
      if (!result) return;
    });
  };

  //item list excel export
  const layoutDefinitionItemList = useMemo(() => {
    return [
      {
        header: String(t('mp.grid.Item', 'Item')),
        columns: [
          {
            binding: 'itemId',
            header: String(t('mp.grid.번호', '번호')),
            width: 105,
            align: 'center',
          },
          {
            binding: 'itemEngNm',
            header: String(t('mp.grid.표준명', '표준명')),
            width: 200,
            align: 'left',
            cellTemplate: (params) => {
              return CommonUtil.safeHtmlContents(params.value);
            },
          },
          {
            binding: 'itemLocalNm',
            header: String(t('mp.grid.Local 명', 'Local 명')),
            width: 200,
            align: 'left',
            cellTemplate: (params) => {
              return CommonUtil.safeHtmlContents(
                (langCd || '').startsWith('zh')
                  ? params.item?.itemCngNm || ''
                  : langCd === 'ko'
                  ? params.item?.itemNm || ''
                  : params.item?.itemEngNm || ''
              );
            },
          },
        ],
      },
      {
        binding: 'mpClsfCdNm',
        header: String(t('mp.grid.분류', '분류')),
        align: 'center',
        width: 106,
      },
      {
        binding: 'mpItemTpCdNm',
        header: String(t('mp.grid.진행단계', '진행단계')),
        width: 100,
      },
      {
        binding: 'itemStatCdNm',
        header: String(t('mp.grid.상태', '상태')),
        width: 100,
        cssClass: 'WijmoPopup',
      },
      {
        header: String(t('mp.grid.개선목적', '개선목적')),
        align: 'center',
        columns: [
          {
            binding: 'iprvClsfLv1CdNm',
            header: String(t('mp.grid.Level 1', 'Level 1')),
            width: 130,
          },
          {
            binding: 'iprvClsfLv2CdNm',
            header: String(t('mp.grid.Level 2', 'Level 2')),
            width: 130,
          },
          {
            binding: 'iprvClsfLv3CdNm',
            header: String(t('mp.grid.Level 3', 'Level 3')),
            width: 130,
          },
        ],
      },
      {
        binding: 'ecoYn',
        header: String(t('mp.grid.NC/EC', 'NC/EC')),
        width: 95,
        align: 'center',
        cellTemplate: (params) => `
        ${params.value ? (params.value === 'Y' ? 'EC' : 'NC') : ''}
      `,
      },
      {
        binding: 'tgtCopCd',
        header: String(t('mp.grid.법인', '법인')),
        width: 97,
      },
      {
        binding: 'procCdNm',
        header: String(t('mp.grid.공정', '공정')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'eqpGr',
        header: String(t('com.label.설비군', '설비군')),
        width: 150,
      },
      {
        binding: 'tgtPlntCd',
        header: String(t('mp.grid.Factory ID', 'Factory ID')),
        width: 100,
      },
      {
        binding: 'tgtPlntCdNm',
        header: String(t('mp.grid.Factory 명', 'Factory 명')),
        width: 100,
      },
      {
        binding: 'eqpNms',
        header: String(t('mp.grid.대표설비', '대표설비')),
        width: 200,
        cssClass: 'WijmoFind',
      },
      {
        binding: 'reqMgrNm',
        header: String(t('mp.grid.담당자', '담당자')),
        width: 110,
        align: 'center',
      },
      {
        binding: 'fstrYn',
        header: String(t('mp.grid.Fast Track 여부', 'Fast Track 여부')),
        width: 140,
        align: 'center',
      },
      {
        binding: 'rawSysNm',
        header: String(t('mp.grid.원천시스템', '원천시스템')),
        width: 117,
        align: 'center',
      },
      {
        binding: 'oldRevCtn',
        header: String(t('mp.grid.변경전 내용', '변경전 내용')),
        width: 114,
      },
      {
        binding: 'newRevCtn',
        header: String(t('mp.grid.변경후 내용', '변경후 내용')),
        width: 114,
      },
      {
        header: String(t('mp.grid.4MCC', '4MCC')),
        columns: [
          {
            binding: 'm4ChgNo',
            header: String(t('mp.grid.발행번호', '발행번호')),
            width: 127,
            align: 'center',
          },
          {
            binding: 'm4StatNm',
            header: String(t('mp.grid.상태', '상태')),
            width: 100,
          },
          {
            binding: 'm4LvNm',
            header: String(t('mp.grid.Level', 'Level')),
            width: 100,
          },
        ],
      },

      {
        binding: 'doneCount',
        header: String(t('mp.grid.수평전개 진척률(%)', '수평전개 진척률(%)')),
        width: 100,
        cellTemplate: (params) => {
          //nvl(round((nvl(t.done_count, 0) / nvl(t.total_count, 1)) * 100, 1), 0)
          const rate = (params.item.doneCount / params.item.totalCount) * 100;
          return `${rate} % (${(params.item.doneCount || 0).toLocaleString()}/${(
            params.item.totalCount || 0
          ).toLocaleString()})`;
        },
      },

      {
        binding: 'mcrcNms',
        header: String(t('mp.grid.분과위원', '분과위원')),
        width: 100,
        cssClass: 'WijmoFind',
      },
      {
        binding: 'chgCrgeUserNm',
        header: String(t('mp.grid.설비담당자', '설비담당자')),
        width: 100,
        align: 'center',
      },

      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.등록자', '등록자')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.등록일자', '등록일자')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        width: 120,
        align: 'center',
      },
    ];
  }, [rowData]);

  //수평전개현황 excel expoert
  const layoutDefinitionHe = useMemo(() => {
    return [
      {
        header: 'ITEM',
        align: 'center',
        columns: [
          {
            binding: 'itemId',
            header: String(t('mp.grid.번호', '번호')),
            width: 105,
            isReadOnly: true,
          },
          {
            binding: 'itemEngNm',
            header: String(t('mp.grid.표준명', '표준명')),
            width: 200,
            align: 'left',
            isReadOnly: true,
            cellTemplate: (params) => {
              return CommonUtil.safeHtmlContents(params.value);
            },
          },
        ],
      },
      {
        binding: 'mpClsfCdNm',
        header: String(t('mp.grid.분류', '분류')),
        align: 'center',
        isReadOnly: true,
        width: 106,
      },
      {
        binding: 'procCdNm',
        header: String(t('mp.grid.공정', '공정')),
        align: 'center',
        isReadOnly: true,
        width: 110,
      },
      {
        binding: 'eqclId',
        header: t('com.label.설비군', '설비군'),
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'mpSftyClsfNm',
        header: t('com.label.remark', 'remark'),
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'tgtCopCd',
        header: t('com.label.대상법인', '대상법인'),
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'tgtPlntCd',
        header: t('com.label.Factory ID', 'Factory ID'),
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'tgtPlntCdNm',
        header: t('com.label.Factory 명', 'Factory 명'),
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'horExplCrgeUserNm',
        header: t('com.label.수평전개 담당자', '수평전개 담당자'),
        align: 'center',
        isReadOnly: true,
      },

      {
        header: String(t('mp.grid.4MCC', '4MCC')),
        columns: [
          {
            binding: 'm4ChgNo',
            header: String(t('mp.grid.발행번호', '발행번호')),
            width: 127,
            align: 'center',
          },
          {
            binding: 'm4StatNm',
            header: String(t('mp.grid.상태', '상태')),
            width: 100,
          },
          {
            binding: 'm4LvNm',
            header: String(t('mp.grid.Level', 'Level')),
            width: 100,
          },
        ],
      },
      {
        header: String(t('mp.grid.설비', '설비')),
        columns: [
          {
            binding: 'eqpId',
            header: String(t('mp.grid.설비ID', '설비ID')),
            width: 127,
            align: 'center',
          },
          {
            binding: 'equipmentName',
            header: String(t('mp.grid.설비명', '설비명')),
            width: 100,
          },
          {
            binding: 'equipmentLevel',
            header: String(t('mp.grid.Level', 'Level')),
            width: 100,
          },
          {
            binding: 'cortCrgeUserNm',
            header: String(t('mp.grid.담당자', '담당자')),
            width: 100,
          },
        ],
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.등록자', '등록자')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.등록일자', '등록일자')),
        width: 120,
        align: 'center',
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        width: 120,
        align: 'center',
      },
    ];
  }, [rowData]);

  //상태변경이력 excel expoert
  const layoutDefinitionHis = useMemo(() => {
    return [
      {
        header: 'ITEM',
        align: 'center',
        columns: [
          {
            binding: 'itemId',
            header: String(t('mp.grid.번호', '번호')),
            width: 105,
            isReadOnly: true,
          },
          {
            binding: 'itemEngNm',
            header: String(t('mp.grid.표준명', '표준명')),
            width: 200,
            align: 'left',
            isReadOnly: true,
            cellTemplate: (params) => {
              return CommonUtil.safeHtmlContents(params.value);
            },
          },
        ],
      },
      {
        header: String(t('com.label.진행단계', '진행단계')),
        binding: 'itemTpCdNm',
        width: 120,
      },
      {
        header: String(t('com.label.상태', '상태')),
        binding: 'itemStatCdNm',
        align: 'left',
        width: 120,
      },
      {
        header: String(t('com.label.이벤트', '이벤트')),
        binding: 'occrEvtNm',
        width: '*',
        align: 'left',
      },
      {
        header: String(t('com.label.비고', '비고')),
        binding: 'rmk',
        align: 'left',
        width: 200,
      },
      {
        header: String(t('com.label.등록자', '등록자')),
        binding: 'dataInsUserNm',
        width: 80,
      },
      {
        header: String(t('com.label.등록일자', '등록일자')),
        binding: 'dataInsDtm',
        width: 100,
      },
    ];
  }, [rowData]);

  const mpClsfCd = new DataMap(dataMapMpSftyClsf, 'cmnCd', 'cmnCdNm');
  const prdnProcCd = new DataMap(dataMapPrdnProcCd, 'cmnCd', 'cmnCdNm');

  //Item 번호, 표준명, Local명  / MP분류 / 설비군 / Remark / rowh/개선 Item 보고서 / 진척율 / (법인 – 플랜트)2
  const layoutDefinition = useMemo(() => {
    const layout = [] as any[];

    let prevHeaderName = '';
    const prevHeaderIdx = 0;

    if (dynamicCol.length > 0) {
      dynamicCol.forEach((item, index) => {
        const words = item?.PLLN_NM?.split(':');
        // Header 추가
        if (prevHeaderName !== words[0]) {
          // prevHeaderIdx = index;
          prevHeaderName = words[0];
          layout.push({ header: words[0], align: 'center', columns: [] });
        }

        const filedNm = "'" + item.PLLN + "'_SATUS";

        // Children Column 추가
        layout[layout.length - 1]['columns'].push({
          header: words[1],
          align: 'center',
          binding: filedNm,
          isReadOnly: true,
          cssClass: 'WijmoTag',
          minWidth: 95,
          width: 95,
          cellTemplate: (ctx) => setColor(ctx),
        });
      });
    }

    return [
      {
        header: 'ITEM',
        align: 'center',
        columns: [
          {
            binding: 'ITEM_ID',
            header: String(t('mp.grid.번호', '번호')),
            width: 95,
            isReadOnly: true,
          },
          {
            binding: 'ITEM_ENG_NM',
            header: String(t('mp.grid.표준명', '표준명')),
            minWidth: 100,
            width: '*',
            align: 'left',
            isReadOnly: true,
            cellTemplate: (params) => {
              return CommonUtil.safeHtmlContents(params.value);
            },
          },
          {
            binding: 'ITEM_LOCAL_NM',
            header: String(t('mp.grid.Local 명', 'Local 명')),
            minWidth: 100,
            width: '*',
            isReadOnly: true,
            align: 'left',
            cellTemplate: (params) => {
              return CommonUtil.safeHtmlContents(
                (langCd || '').startsWith('zh')
                  ? params.item?.ITEM_CNG_NM || ''
                  : langCd === 'ko'
                  ? params.item?.ITEM_NM || ''
                  : params.item?.ITEM_ENG_NM || ''
              );
            },
          },
        ],
      },
      {
        binding: 'MP_CLSF_CD',
        header: String(t('mp.grid.분류', '분류')),
        align: 'center',
        isReadOnly: true,
        width: 70,
        dataMap: mpClsfCd,
      },
      {
        binding: 'PROC_CD',
        header: String(t('mp.grid.공정', '공정')),
        align: 'center',
        isReadOnly: true,
        width: 90,
        dataMap: prdnProcCd,
      },
      {
        binding: 'EQCL_ID',
        header: t('com.label.설비군', '설비군'),
        align: 'center',
        width: 90,
        isReadOnly: true,
      },
      {
        binding: 'MP_SFTY_CLSF',
        isReadOnly: !isMpMntAuth,
        header: String(t('com.label.remark', 'remark')),
        align: 'left',
        width: 130,
        dataType: DataType.Array,
        editor: new input.MultiSelect(document.createElement('div'), {
          placeholder: '선택',
          showSelectAllCheckbox: true,
          maxHeaderItems: 5,
          headerFormat: '{count:n0} items', //Cell에 비해 데이터 내용이 많을 경우....원하는 형태 처리.
          itemsSource: code?.mpSftyClsf, //배열
          displayMemberPath: 'cmnCdNm', //목록에 표시되는  항목의 속성 //
          selectedValuePath: 'cmnCd',
        }),
      },
      {
        header: t('mp.label.개조/개선', '개조/개선'),
        columns: [
          {
            binding: 'ATCH_FILE_GR_ID',
            header: t('mp.label.Item 보고서', 'Item 보고서'),
            isReadOnly: true,
            width: 80,
            cellTemplate: (params) => `
            <div class="${params.item.ATCH_FILE_GR_ID_RPT_IMG_CNT > 0 ? 'fileDiv' : ''}">
              <span><em>${(params.item.ATCH_FILE_GR_ID_RPT_CNT || 0).toLocaleString()}</em></span>
            </div> 
          `,
            /*cellTemplate: (params) => {
              if (params.item.ATCH_FILE_GR_ID_RPT_CNT > 0)
                return `${
                  params.item.ATCH_FILE_GR_ID_RPT_CNT || ''
                }<Button id="ATCH_FILE_GR_ID" />`;
            },*/
          },
        ],
      },
      {
        header: t('mp.label.수평전개', '수평전개'),
        columns: [
          {
            binding: 'ITEM_PROG',
            header: t('mp.label.진척률(%)', '진척률(%)'),
            align: 'center',
            isReadOnly: true,
            width: 95,
            cellTemplate: (params) =>
              `${params.item.PROGRESS_RATE}% (${(params.item.DONE_COUNT || 0).toLocaleString()}/${(
                params.item.TOTAL_COUNT || 0
              ).toLocaleString()})`,
            // wordWrap: true,
          },
        ],
      },
      ...layout,
    ];
  }, [code, dynamicCol]);

  return (
    <>
      <MPCommonSearch type={'summary'} onHandleSearch={handleSearch} />
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('mp.label.수평전개 Item', '수평전개 Item')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData ? rowData.length : 0}</span>
            {t('com.label.건', '건')}
            {/* {t('com.label.진척률', '진척률')}<span>00</span>% */}
          </span>
        </SubTitleGroup>

        <ControlBtnGroup>
          <Button css={IconButton.button} className="Exceldown" disableRipple onClick={exportExcel}>
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="Exceldown" disableRipple onClick={exportGrids}>
            {t('com.button.Raw Data', 'Raw Data')}
          </Button>
          {/*<Button*/}
          {/*  css={IconButton.button}*/}
          {/*  className="refresh"*/}
          {/*  disableRipple*/}
          {/*  onClick={() => handleSearch(mpItemCondition)}*/}
          {/*>*/}
          {/*  {t('com.button.새로고침', '새로고침')}*/}
          {/*</Button>*/}
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={560}
        allowPinning={false}
        initialized={initialized}
        isReadOnly={false}
        cellEditEnded={onCellEditEnded}
        align="center"
        frozenColumns={3}
      />

      <div style={{ display: 'none' }}>
        <CustomGrid
          layoutDefinition={layoutDefinitionHis}
          rowData={rowDataHis}
          initialized={initializedHis}
          height={450}
          allowPinning={false}
          isReadOnly={true}
          align="center"
        />
        <CustomGrid
          layoutDefinition={layoutDefinitionHe}
          rowData={rowDataHeExport}
          initialized={initializedHe}
          height={450}
          allowPinning={false}
          isReadOnly={true}
          align="center"
        />
        <CustomGrid
          layoutDefinition={layoutDefinitionItemList}
          rowData={rowDataItemList}
          initialized={initializedItemList}
          height={450}
          allowPinning={false}
          isReadOnly={true}
          align="center"
        />
      </div>

      {mpMyBookmarkModalOpen && (
        <MpMyBookmarkModal
          open={mpMyBookmarkModalOpen}
          close={() => setMpMyBookmarkModalOpen(false)}
        />
      )}

      {isOpenSftyClsfModal && (
        <MpSftyClsfPopUp
          open={isOpenSftyClsfModal}
          close={() => setOpenSftyClsfModal(false)}
          title={'remark'}
          initParam={{
            mpSftyClsf: getMpsftyClsf,
            mpItemId: getMpItemId,
          }}
          onCallbackData={(data) => console.log(data)}
        />
      )}

      {isOpenFileImgViewModal && (
        <MpFileImgViewPopUp
          open={isOpenFileImgViewModal}
          close={() => {
            setOpenFileImgViewModal(false);
          }}
          title={'개조/개선 item 보고서'}
          fileData={getOenFileImgViewAtchFileGrId}
          fileType={'MP_ITEM_RPT'}
          onCallbackData={() => setOpenFileImgViewAtchFileGrId('')}
        />
      )}

      {isOpenEmailModal && (
        <AutoSendEmailModal
          open={isOpenEmailModal}
          close={() => setOpenEmailModal(false)}
          condition={{
            bizName: BizName.MP, // 이메일업무구분코드
            bmkAtcTpCd: BmkAtcTpCd.MP_LIST, //즐겨찾기항목유형코드
          }}
        />
      )}
    </>
  );
};

export default MpTrackingManagementPage;
